import React, { ComponentType, FunctionComponent, useState } from 'react';
import { useNotify, useRefresh, useTranslate } from 'react-admin';
import { get } from 'lodash';
import { Checkbox, FormControlLabel, TextField } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { httpClient } from '../../providers/dataProvider';
import useConfig from '../../hooks/useConfig';
import { getCurrentUserId } from '../../helpers/auth';
import { getLocaleDateString } from '../../helpers/date';

const useStyles = makeStyles({
  checkbox: {
    marginTop: '8px',
  },
  givenOn: {
    fontSize: '14px',
  },
});

/**
 * "Signature contrats" tab of the contract signatory admin's page
 * (`contract_signatory`): the consent text to the automatic signature of
 * contracts, in a disabled field, then a checkbox giving (checked) or
 * withdrawing (unchecked) that consent -- in the admin's own session only,
 * read-only for the other admins -- and "Consentement donné le dd/mm/yyyy"
 * (`contract_signature_consent_given_at`) while it's in force. Withdrawing
 * sets the contracts awaiting their signature back to "Manuelle" (see the
 * API's User#revoke_contract_signature_consent!).
 */
function withContractSignatureConsent(
  _Component: ComponentType<any>
): FunctionComponent {
  const Extension: FunctionComponent<Record<string, any>> = (props) => {
    const { record, source } = props;
    const classes = useStyles();
    const t = useTranslate();
    const notify = useNotify();
    const refresh = useRefresh();
    const config = useConfig();
    const [loading, setLoading] = useState(false);

    if (!record?.contract_signatory) return null;

    const ownSession = String(record.id) === String(getCurrentUserId());
    const consentGivenAt = record[source];

    const toggleConsent = async (consent: boolean) => {
      setLoading(true);

      try {
        await httpClient(`${config.apiUrl}/contract_signature_consent`, {
          method: consent ? 'POST' : 'DELETE',
        });
        refresh();
      } catch (error) {
        const key = `flash.error.${get(error, 'body.message')}`;
        notify(t(key) !== key ? key : 'notify.error.update', 'error');
      } finally {
        setLoading(false);
      }
    };

    return (
      <>
        <TextField
          label={t('resource.contracts.consent.title')}
          value={t('resource.contracts.consent.text', {
            first_name: record.first_name,
            last_name: record.last_name,
          })}
          variant="filled"
          multiline
          fullWidth
          disabled
        />
        <FormControlLabel
          className={classes.checkbox}
          control={
            <Checkbox
              color="primary"
              checked={Boolean(consentGivenAt)}
              disabled={!ownSession || loading}
              onChange={(event) => toggleConsent(event.target.checked)}
            />
          }
          label={t('resource.contracts.consent.checkbox')}
        />
        {consentGivenAt && (
          <div className={classes.givenOn}>
            {t('resource.contracts.consent.given_on', {
              date: getLocaleDateString(consentGivenAt),
            })}
          </div>
        )}
      </>
    );
  };

  Extension.displayName = 'withExtension(ContractSignatureConsent)';

  return Extension;
}

export default withContractSignatureConsent;
